import React, { useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { Info, Share2 } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import {
  ScoreRing,
  VerdictTag,
  TrendChip,
  ScoreSparkline,
  SubScoreRows,
  FundraisingRow,
  ScoreCTAEmptyState,
  IRSInfoSheet,
  SectionHeader,
  type RiskSummary,
} from './atoms';
import type { EvidenceLabel } from '@/lib/scoring/score-display';
import type { VentureLabel } from '@/lib/scoring/methodology';
import type { CommitmentSummary } from '@/lib/state/inverio-store';

interface IRSScoreCardProps {
  // Identity
  companyName?: string;
  tagline?: string;
  description?: string;
  // Score
  finalScore?: number;
  ventureLabel?: VentureLabel | string;
  ventureScore?: number;
  evidenceLabel?: EvidenceLabel;
  evidenceConfidence?: number;
  evidenceSourceFlavor?: string;
  dealReadinessPercent?: number;
  dealReadinessPresent?: number;
  dealReadinessTotal?: number;
  riskSummary?: RiskSummary | string;
  // Progression (optional — enables TrendChip + Sparkline)
  previousScore?: number;
  weeklyScores?: number[];
  trendUnit?: 'week' | 'month';
  // Fundraising (optional)
  commitmentSummary?: CommitmentSummary | null;
  isActivelyRaising?: boolean;
  stage?: string;
  // CTAs (empty state)
  onUploadDeck?: () => void;
  onConnectDataRoom?: () => void;
  onScoringInterview?: () => void;
  // Share — when provided, renders a small share icon at bottom-right
  onShare?: () => void;
}

const SECTION_GAP = 16;

const STAGE_LABEL: Record<string, string> = {
  'pre-seed': 'Pre-Seed',
  'seed': 'Seed',
  'series-a': 'Series A',
  'series-b': 'Series B',
  'other': 'Other',
};

function formatStageLabel(stage: string): string {
  return STAGE_LABEL[stage] ?? stage;
}

function ColumnLabel({ text, colors }: { text: string; colors: any }) {
  return (
    <Text
      style={{
        color: colors.text.tertiary,
        fontSize: 9,
        fontWeight: '700',
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        marginBottom: 8,
      }}
      numberOfLines={1}
    >
      {text}
    </Text>
  );
}

export function IRSScoreCard({
  companyName,
  tagline,
  description,
  finalScore,
  ventureLabel,
  ventureScore,
  evidenceLabel,
  evidenceConfidence,
  evidenceSourceFlavor,
  dealReadinessPercent,
  dealReadinessPresent,
  dealReadinessTotal,
  riskSummary,
  previousScore,
  weeklyScores,
  trendUnit = 'week',
  commitmentSummary,
  isActivelyRaising,
  stage,
  onUploadDeck,
  onConnectDataRoom,
  onScoringInterview,
  onShare,
}: IRSScoreCardProps) {
  const colors = useThemeColors();
  const [infoOpen, setInfoOpen] = useState(false);
  const isEmpty = finalScore === undefined;
  const showFundraising = !!(commitmentSummary || isActivelyRaising);
  const showProgression =
    previousScore !== undefined || (weeklyScores && weeklyScores.length >= 2);
  const blurb = (description && description.trim()) || tagline;

  const cardStyle = {
    backgroundColor: colors.surface.primary,
    borderWidth: 1,
    borderColor: isEmpty ? colors.border.medium : colors.border.light,
    borderRadius: 16,
    padding: 16,
    ...(isEmpty ? ({ borderStyle: 'dashed' } as object) : {}),
  };

  // ── Header band ───────────────────────────────────────────────────────
  const Header = (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 14,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <Pressable
          onPress={() => setInfoOpen(true)}
          hitSlop={10}
          accessibilityRole="button"
          accessibilityLabel="Methodology and definitions"
          style={{
            width: 18,
            height: 18,
            borderRadius: 9,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Info size={14} color={colors.text.tertiary} />
        </Pressable>
        <Text
          style={{
            color: colors.text.tertiary,
            fontSize: 10,
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: 1.6,
          }}
        >
          Inverio Scorecard
        </Text>
      </View>
      {companyName && (
        <Text
          style={{
            color: colors.text.primary,
            fontSize: 13,
            fontWeight: '700',
            letterSpacing: -0.1,
            flex: 1,
            marginLeft: 12,
            textAlign: 'right',
          }}
          numberOfLines={1}
        >
          {companyName}
        </Text>
      )}
    </View>
  );

  // ── Empty state ───────────────────────────────────────────────────────
  if (isEmpty) {
    return (
      <View style={cardStyle}>
        {Header}
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: SECTION_GAP }}>
          <ScoreRing size="md" showLabelText />
          <View style={{ flex: 1, marginLeft: 16 }}>
            {blurb ? (
              <Text style={{ color: colors.text.secondary, fontSize: 12, lineHeight: 16 }} numberOfLines={4}>
                {blurb}
              </Text>
            ) : (
              <Text style={{ color: colors.text.secondary, fontSize: 12, lineHeight: 18 }}>
                Get your investment readiness score by providing data about your startup.
              </Text>
            )}
          </View>
        </View>
        <ScoreCTAEmptyState
          onUploadDeck={onUploadDeck}
          onConnectDataRoom={onConnectDataRoom}
          onScoringInterview={onScoringInterview}
        />
        <IRSInfoSheet visible={infoOpen} onClose={() => setInfoOpen(false)} />
      </View>
    );
  }

  // ── Scored state — 3-column body with sub-scores as a contained sub-card
  return (
    <View style={cardStyle}>
      {Header}

      {/* Body — 3-column on web, stacked on mobile */}
      {isWeb ? (
        // Web: Ring | Sub-scores | About (3-column)
        <View style={{ flexDirection: 'row', alignItems: 'stretch' }}>
          <View style={{ alignItems: 'center' }}>
            <ColumnLabel text="Overall Score" colors={colors} />
            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
              <ScoreRing
                score={finalScore}
                ventureLabel={ventureLabel}
                size="md"
                showLabelText
              />
            </View>
          </View>
          <View
            style={{
              marginLeft: 12,
              paddingLeft: 12,
              borderLeftWidth: 1,
              borderLeftColor: colors.border.light,
            }}
          >
            <ColumnLabel text="Sub-scores" colors={colors} />
            <View
              style={{
                alignSelf: 'flex-start',
                backgroundColor: colors.surface.secondary,
                borderWidth: 1,
                borderColor: colors.border.light,
                borderRadius: 10,
                paddingHorizontal: 10,
                paddingVertical: 4,
              }}
            >
              <SubScoreRows
                ventureLabel={ventureLabel}
                ventureScore={ventureScore}
                evidenceLabel={evidenceLabel}
                evidenceConfidence={evidenceConfidence}
                evidenceSourceFlavor={evidenceSourceFlavor}
                dealReadinessPercent={dealReadinessPercent}
                dealReadinessPresent={dealReadinessPresent}
                dealReadinessTotal={dealReadinessTotal}
                riskSummary={riskSummary}
                density="compact"
                layout="inline"
              />
            </View>
          </View>
          {blurb ? (
            <View
              style={{
                flex: 1,
                marginLeft: 12,
                paddingLeft: 12,
                borderLeftWidth: 1,
                borderLeftColor: colors.border.light,
              }}
            >
              <ColumnLabel text="About" colors={colors} />
              <View style={{ flex: 1 }}>
                <Text
                  style={{ color: colors.text.secondary, fontSize: 12, lineHeight: 16 }}
                >
                  {blurb}
                </Text>
              </View>
            </View>
          ) : null}
        </View>
      ) : (
        // Mobile: Ring + About on top row, Sub-scores card full-width below
        <View>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <ScoreRing
              score={finalScore}
              ventureLabel={ventureLabel}
              size="md"
              showLabelText
            />
            {blurb ? (
              <View
                style={{
                  flex: 1,
                  marginLeft: 14,
                  paddingLeft: 14,
                  borderLeftWidth: 1,
                  borderLeftColor: colors.border.light,
                }}
              >
                <ColumnLabel text="About" colors={colors} />
                <Text
                  style={{ color: colors.text.secondary, fontSize: 12, lineHeight: 16 }}
                  numberOfLines={5}
                >
                  {blurb}
                </Text>
              </View>
            ) : null}
          </View>
          <View
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTopWidth: 1,
              borderTopColor: colors.border.light,
            }}
          >
            <ColumnLabel text="Sub-scores" colors={colors} />
            <View
              style={{
                backgroundColor: colors.surface.secondary,
                borderWidth: 1,
                borderColor: colors.border.light,
                borderRadius: 10,
                paddingHorizontal: 12,
                paddingVertical: 6,
              }}
            >
              <SubScoreRows
                ventureLabel={ventureLabel}
                ventureScore={ventureScore}
                evidenceLabel={evidenceLabel}
                evidenceConfidence={evidenceConfidence}
                evidenceSourceFlavor={evidenceSourceFlavor}
                dealReadinessPercent={dealReadinessPercent}
                dealReadinessPresent={dealReadinessPresent}
                dealReadinessTotal={dealReadinessTotal}
                riskSummary={riskSummary}
                density="default"
                layout="inline"
              />
            </View>
          </View>
        </View>
      )}

      {/* Progression band */}
      {(showProgression || finalScore !== undefined) && (
        <View style={{ marginTop: SECTION_GAP }}>
          {showProgression ? (
            <SectionHeader label="Progression" />
          ) : (
            <View style={{ height: 1, backgroundColor: colors.border.light, marginBottom: 10 }} />
          )}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            <VerdictTag score={finalScore} ventureLabel={ventureLabel} />
            {previousScore !== undefined && (
              <TrendChip current={finalScore} previous={previousScore} unit={trendUnit} />
            )}
            {weeklyScores && weeklyScores.length >= 2 && (
              <View style={{ marginLeft: 'auto' }}>
                <ScoreSparkline data={weeklyScores} height={22} />
              </View>
            )}
          </View>
        </View>
      )}

      {/* Fundraising band */}
      {showFundraising && (
        <View style={{ marginTop: SECTION_GAP }}>
          <SectionHeader
            label="Fundraising"
            trailing={
              stage ? (
                <View
                  style={{
                    backgroundColor: colors.primary[50],
                    paddingHorizontal: 8,
                    paddingVertical: 3,
                    borderRadius: 999,
                  }}
                >
                  <Text
                    style={{
                      color: colors.primary[600],
                      fontSize: 10,
                      fontWeight: '700',
                      letterSpacing: 0.3,
                    }}
                    numberOfLines={1}
                  >
                    {formatStageLabel(stage)}
                  </Text>
                </View>
              ) : undefined
            }
          />
          <FundraisingRow
            commitmentSummary={commitmentSummary ?? null}
            isActivelyRaising={isActivelyRaising}
            stage={stage}
            variant="bare"
          />
        </View>
      )}

      {/* Share icon — bottom-right corner of the card */}
      {onShare && (
        <Pressable
          onPress={onShare}
          hitSlop={10}
          accessibilityRole="button"
          accessibilityLabel="Share scorecard"
          style={{
            position: 'absolute',
            right: 10,
            bottom: 10,
            width: 28,
            height: 28,
            borderRadius: 8,
            backgroundColor: colors.surface.secondary,
            borderWidth: 1,
            borderColor: colors.border.light,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Share2 size={13} color={colors.text.secondary} />
        </Pressable>
      )}

      <IRSInfoSheet visible={infoOpen} onClose={() => setInfoOpen(false)} />
    </View>
  );
}
